/**
 * Transform.ts
 * 
 * Centralizes all coordinate transformations between:
 * - World Space (WASM, 96 DPI pixels)
 * - Canvas Space (CSS pixels, scaled/zoomed)
 * - Device Space (Physical pixels, DPR-scaled)
 * 
 * This ensures glyphs, caret, selections, and hit-testing all use
 * identical coordinate math. No more scattered multiplications.
 */

export type Transform = {
  /** Scale factor from world px to canvas px (includes zoom) */
  scale: number;
  
  /** Device pixel ratio for high-DPI displays */
  dpr: number;
  
  /** Page origin X in canvas px (for centering) */
  offsetX: number;
  
  /** Page origin Y in canvas px (for centering) */
  offsetY: number;
  
  /** Transform world X coordinate to canvas X */
  worldToCanvasX: (x: number) => number;
  
  /** Transform world Y coordinate to canvas Y */
  worldToCanvasY: (y: number) => number;
  
  /** Transform canvas X coordinate to world X (for hit-testing) */
  canvasToWorldX: (x: number) => number;
  
  /** Transform canvas Y coordinate to world Y (for hit-testing) */
  canvasToWorldY: (y: number) => number;
};

export interface TransformOptions {
  /** Page width in world pixels (from PageMetrics) */
  pageWidthPx: number;
  
  /** Page height in world pixels (from PageMetrics) */
  pageHeightPx: number;
  
  /** Canvas CSS width */
  canvasCssWidth: number;
  
  /** Canvas CSS height */
  canvasCssHeight: number;
  
  /** Zoom level (1.0 = 100%, 1.5 = 150%) */
  zoom: number;
  
  /** Device pixel ratio (window.devicePixelRatio) */
  dpr: number;
  
  /** Page origin X in canvas px (default: 0) */
  pageOriginX?: number;
  
  /** Page origin Y in canvas px (default: 0) */
  pageOriginY?: number;
}

/**
 * Creates a transform object for converting between world and canvas coordinates.
 * 
 * The scale factor matches the existing renderer's coordinateScale:
 * coordinateScale = canvasWidth / wasmPageWidth
 * 
 * After creating a transform, use it for ALL coordinate operations:
 * - Glyph rendering: worldToCanvasX/Y
 * - Caret placement: worldToCanvasX/Y
 * - Mouse hit-testing: canvasToWorldX/Y
 * - Selection rects: worldToCanvasX/Y
 * 
 * @param opts - Transform configuration
 * @returns Transform object with conversion functions
 */
export function makeTransform(opts: TransformOptions): Transform {
  const {
    pageWidthPx,
    canvasCssWidth,
    zoom,
    dpr,
    pageOriginX = 0,
    pageOriginY = 0,
  } = opts;
  
  // Match existing renderer: coordinateScale = canvasWidth / wasmPageWidth
  // If zoom is already baked into canvasCssWidth, set zoom=1. Otherwise multiply.
  const coordinateScale = (canvasCssWidth / pageWidthPx) * zoom;
  
  const scale = coordinateScale; // world px → canvas px (logical)
  const offsetX = pageOriginX;
  const offsetY = pageOriginY;
  
  return {
    scale,
    dpr,
    offsetX,
    offsetY,
    
    worldToCanvasX: (x: number) => offsetX + x * scale,
    worldToCanvasY: (y: number) => offsetY + y * scale,
    canvasToWorldX: (x: number) => (x - offsetX) / scale,
    canvasToWorldY: (y: number) => (y - offsetY) / scale,
  };
}

/**
 * Prepares a canvas for high-DPI rendering.
 * 
 * Sets the backing store to physical pixels (CSS * DPR) and applies
 * a DPR transform so all subsequent drawing can use logical canvas pixels.
 * 
 * Call this ONCE per frame before any drawing operations.
 * 
 * @param canvas - HTML canvas element
 * @param ctx - 2D rendering context
 * @param cssWidth - Canvas CSS width
 * @param cssHeight - Canvas CSS height
 * @param dpr - Device pixel ratio
 */
export function prepareCanvasForHighDPI(
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  cssWidth: number,
  cssHeight: number,
  dpr: number
): void {
  // Set backing store to physical pixels
  canvas.width = Math.round(cssWidth * dpr);
  canvas.height = Math.round(cssHeight * dpr);

  // Apply DPR transform once
  // After this, all drawing uses logical canvas px
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

  // ✅ High-quality text rendering settings
  // Disable image smoothing for crisp glyph rendering
  ctx.imageSmoothingEnabled = false;

  // Set font rendering hints if available
  if ('fontSmooth' in ctx) {
    (ctx as any).fontSmooth = 'always';
  }
  if ('textBaseline' in ctx) {
    ctx.textBaseline = 'alphabetic';
  }
  if ('textAlign' in ctx) {
    ctx.textAlign = 'left';
  }
}


